import type { AudioFileSource } from '@/features/media-file/domain/models/media-file'
import type { Voice } from '@/features/voice/domain/models/voice.ts'
import useTracksStore, {
  TrackType,
  VOICE_TRACK_ID,
  trackSelector,
} from '@/shared/store/tracks.store.tsx'
import useMediaStore from '@/shared/store/media.store.tsx'
import type { Clip } from '../models/clip'
import { updateClip } from './update-clip'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url'
import { createSTSSpeech } from '@/features/speech/domain/use-cases/create-sts-speech'
import { getSourceId } from '@/features/media-file/domain/use-cases/get-source-id'

const addSpeechClip = async (
  scriptID: string,
  source: AudioFileSource,
  voice: Voice,
  expressive = false
): Promise<void> => {
  const fileId = await getSourceId(source)
  if (!fileId) throw new Error('File ID not found')
  const { addClip } = useTracksStore.getState()
  const { addMedia } = useMediaStore.getState()
  const track = trackSelector(VOICE_TRACK_ID)(useTracksStore.getState())

  // Calculate the last available position to stack clips
  const lastAvailablePosition = track.clips.reduce((acc, clip) => {
    if (clip.startAt === undefined) return acc
    const clipEnding = clip.startAt + clip.duration + clip.endOffset
    if (clipEnding > acc) {
      return clipEnding
    }
    return acc
  }, 0)

  const trackId = crypto.randomUUID()
  const clip = {
    id: trackId,
    mediaId: trackId,
    speechType: 'sts',
    type: TrackType.Voices,
    trackId: VOICE_TRACK_ID,
    name: voice.name,
    startAt: lastAvailablePosition, // Stack clips instead of placing at 0
    startOffset: 0,
    endOffset: 0,
    duration: source.mediaFile?.duration ?? 0,
    fadeIn: 0,
    fadeOut: 0,
  } satisfies Clip

  addClip(VOICE_TRACK_ID, clip)
  const result = await createSTSSpeech(scriptID, fileId, voice, expressive)
  const buffer = await getAudioBufferFromAudioUrl(result.speechUrl)
  addMedia(result.speechId, buffer)
  updateClip(clip.id, {
    mediaId: result.speechId,
    src: result.speechUrl,
    duration: buffer.duration,
  })
}

export default addSpeechClip
